import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { List, X, SignIn, SignOut, Plus, UserCircle, House, MagnifyingGlass, Heart, ChatCircleText, GearSix, ChartLineUp, Buildings } from "@phosphor-icons/react";

// Mobile navigation drawer.
// Opens from the right on <md screens.
// Consumed by the Header via a hamburger trigger.
export const MobileNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const doLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-[#C8A24A]"
          data-testid="mobile-nav-trigger"
          aria-label="Abrir menú"
        >
          <List size={22} weight="bold" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="bg-[#0A0A0A] border-l border-[#C8A24A]/20 text-[#C8A24A] p-0 w-[85vw] max-w-sm"
        data-testid="mobile-nav-panel"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#C8A24A]/20">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center">
            <img src="/logo.png" alt="ROCAFUERTE" className="h-10 w-auto rounded-md" />
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="p-2 text-[#C8A24A]/70 hover:text-[#C8A24A]"
            aria-label="Cerrar menú"
            data-testid="mobile-nav-close"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-6 flex flex-col gap-1 text-base font-medium">
          <MobileNavLink to="/" onClick={() => setOpen(false)} icon={<House size={18} />} label="Inicio" testid="mobile-nav-home" end />
          <MobileNavLink to="/propiedades" onClick={() => setOpen(false)} icon={<MagnifyingGlass size={18} />} label="Propiedades" testid="mobile-nav-properties" />
          <MobileNavLink to="/favoritos" onClick={() => setOpen(false)} icon={<Heart size={18} />} label="Favoritos" testid="mobile-nav-favorites" />
        </nav>

        <div className="p-6 pt-2 border-t border-[#C8A24A]/20 mt-2 flex flex-col gap-2">
          {user && user !== false ? (
            <>
              <Button
                onClick={() => go("/dashboard/nueva")}
                className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12 justify-start px-4"
                data-testid="mobile-nav-publish"
              >
                <Plus size={18} weight="bold" className="mr-2" /> Publicar propiedad
              </Button>
              <Button
                variant="ghost"
                onClick={() => go("/dashboard")}
                className="w-full text-[#C8A24A] hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-dashboard"
              >
                <UserCircle size={20} weight="duotone" className="mr-2" />
                {user.name?.split(" ")[0] || "Panel"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => go("/dashboard/leads")}
                className="w-full text-[#C8A24A]/80 hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-leads"
              >
                <ChatCircleText size={18} weight="duotone" className="mr-2" /> Lead Center
              </Button>
              <Button
                variant="ghost"
                onClick={() => go("/dashboard/analytics")}
                className="w-full text-[#C8A24A]/80 hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-analytics"
              >
                <ChartLineUp size={18} weight="duotone" className="mr-2" /> Analíticas
              </Button>
              <Button
                variant="ghost"
                onClick={() => go("/dashboard/agencia")}
                className="w-full text-[#C8A24A]/80 hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-agency"
              >
                <Buildings size={18} weight="duotone" className="mr-2" /> Agencia
              </Button>
              <Button
                variant="ghost"
                onClick={() => go("/dashboard/cuenta")}
                className="w-full text-[#C8A24A]/80 hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-account"
              >
                <GearSix size={18} weight="duotone" className="mr-2" /> Configuración
              </Button>
              <Button
                variant="ghost"
                onClick={doLogout}
                className="w-full text-[#C8A24A]/70 hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-logout"
              >
                <SignOut size={18} className="mr-2" /> Cerrar sesión
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => go("/login")}
                className="w-full text-[#C8A24A] hover:bg-[#C8A24A]/5 h-12 justify-start px-4"
                data-testid="mobile-nav-login"
              >
                <SignIn size={18} className="mr-2" /> Iniciar sesión
              </Button>
              <Button
                onClick={() => go("/registro")}
                className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12"
                data-testid="mobile-nav-register"
              >
                Soy agente
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MobileNavLink = ({ to, onClick, icon, label, testid, end }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
        isActive
          ? "bg-[#C8A24A]/10 text-[#C8A24A]"
          : "text-[#C8A24A]/70 hover:bg-[#C8A24A]/5 hover:text-[#C8A24A]"
      }`
    }
    data-testid={testid}
  >
    {icon}
    <span>{label}</span>
  </NavLink>
);
